import React from "react";
import {Link} from "react-router-dom";
import {css} from "@emotion/css";
import {Icon, Text, styled, theme} from "@reside/ui";
import {TableUserDto, UserPermissionEnum} from "../../../../utils/types/user";
import {
  getPermissionStats,
  PERMISSIONS_MANAGEMENT_LIST_PATH,
  QuicksightPermissionStatsInput,
} from "./permissionsUserDetail.helpers";
import {PermissionsUserMeta} from "./PermissionsUserMeta";

type Props = Readonly<{
  user: TableUserDto;
  activePermissions: ReadonlyArray<UserPermissionEnum>;
  includeOtherGroup: boolean;
  /** When false, granted/not-granted show a placeholder until Quicksight loads. */
  quicksightStatsReady: boolean;
  /** Dashboard selected/total counts; only applied when `quicksightStatsReady`. */
  quicksightStats?: QuicksightPermissionStatsInput;
}>;

export const PermissionsUserDetailHeader = ({
  user,
  activePermissions,
  includeOtherGroup,
  quicksightStatsReady,
  quicksightStats,
}: Props) => {
  const categoryCount = getPermissionStats(
    activePermissions,
    includeOtherGroup,
  ).groups;
  const stats = quicksightStatsReady
    ? getPermissionStats(activePermissions, includeOtherGroup, quicksightStats)
    : null;

  return (
    <HeaderSection>
      <BackLink to={PERMISSIONS_MANAGEMENT_LIST_PATH}>
        <Icon name="left" size={14} />
        Back to list
      </BackLink>
      <ProfileCard>
        <Text fontWeight="bold" className={styles.name}>
          {user.firstName} {user.lastName}
        </Text>
        <PermissionsUserMeta user={user} />
        <StatsRow>
          <StatCard>
            <StatValue tone="granted" isLoading={!stats}>
              {stats ? stats.granted : "—"}
            </StatValue>
            <StatLabel>Permissions granted</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue tone="notGranted" isLoading={!stats}>
              {stats ? stats.notGranted : "—"}
            </StatValue>
            <StatLabel>Not granted</StatLabel>
          </StatCard>
          <StatCard>
            <StatValue tone="groups">{categoryCount}</StatValue>
            <StatLabel>Categories</StatLabel>
          </StatCard>
        </StatsRow>
      </ProfileCard>
    </HeaderSection>
  );
};

const styles = {
  name: css`
    font-size: 24px;
    line-height: 36px;
    margin: 0 0 8px;
  `,
};

const HeaderSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const BackLink = styled(Link)`
  align-items: center;
  color: ${theme.color.primary};
  display: inline-flex;
  font-size: 16px;
  font-weight: 500;
  gap: 8px;
  text-decoration: none;
  width: fit-content;

  &:hover {
    color: ${theme.color.primary};
  }
`;

const ProfileCard = styled.div`
  background: #ffffff;
  border: 1px solid ${theme.color.gray10};
  border-radius: 8px;
  padding: 32px;
`;

const StatsRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 32px;
`;

const StatCard = styled.div`
  background: #f0f5ff;
  border-radius: 8px;
  flex: 1;
  min-width: 0;
  padding: 16px 16px;
  text-align: center;
`;

const StatValue = styled.div<{
  tone: "granted" | "notGranted" | "groups";
  isLoading?: boolean;
}>`
  color: ${({isLoading, tone}) =>
    isLoading
      ? theme.color.gray150
      : tone === "granted"
      ? theme.color.green100
      : tone === "groups"
      ? theme.color.primary
      : theme.color.darkBlue100};
  font-size: 24px;
  font-weight: 700;
  line-height: 24px;
`;

const StatLabel = styled.div`
  color: ${theme.color.gray150};
  font-size: 14px;
  line-height: 20px;
  margin-top: 8px;
`;
